import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { IProduct, InternalProduct } from "../../../models/catalogue";
import { isoProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { RootProductCompareDrawer } from "../RootProductCompareDrawer";
import { RootProductCompareModal } from "../RootProductCompareModal";
import { defaults } from "../defaults";
import { IModularConfiguratorOptionSet } from "../models";
import { setSelectedRootProduct } from "../reducers";
import { offeredRootProductsSelector, rootProductSelector } from "../selectors";
import { getDefaultRootProduct } from "../utils";

import styles from "./index.module.scss";

function getRootProductTitle(
    rootProduct: InternalProduct,
    isPlpPanelStyle: boolean | undefined,
): string {
    if (isPlpPanelStyle) {
        return rootProduct.subtitle || rootProduct.title;
    } else {
        if (rootProduct.attributes.model_title?.value) {
            return rootProduct.attributes.model_title.value.toString();
        }
        return rootProduct.title;
    }
}

function showCompareLink(
    isPlpPanelStyle: boolean | undefined,
    optionSet: IModularConfiguratorOptionSet,
): boolean {
    if (
        !isPlpPanelStyle ||
        !optionSet.root_product_compare_modal ||
        optionSet.root_product_compare_modal.products.length === 0
    ) {
        return false;
    }

    return true;
}

export const RadioRootProductSelector = (
    props: Immutable<{
        optionSet: IModularConfiguratorOptionSet;
        isPlpPanelStyle?: boolean;
        useCompareDrawerCenter?: boolean;
    }>,
): React.ReactNode => {
    const [isPanelOpen, setIsPanelOpen] = React.useState(false);
    const compareTriggerRef = React.useRef<HTMLButtonElement>(null);

    const dispatch = useAppDispatch();
    const configuratorState =
        useAppSelector((rootState) => rootState.configurator) || defaults;
    const rootProducts = configuratorState.entities.rootProducts;
    const offeredProducts = offeredRootProductsSelector(configuratorState);
    const selectedRootProduct = rootProductSelector(configuratorState);

    const onOpen = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsPanelOpen(true);
    };

    const onCloseButtonClick = (
        removeStaticFromBody: () => void,
        removePanelStateToBody: () => void,
        showFinanceBlock: () => void,
    ) => {
        setIsPanelOpen(false);
        // Execute cleanup callbacks after state update
        removeStaticFromBody();
        removePanelStateToBody();
        showFinanceBlock();
    };

    const onSelectRootProduct = (product: IProduct) => {
        dispatch(setSelectedRootProduct(product));
    };

    if (rootProducts.length <= 1) {
        return null;
    }

    const selectedProductID = selectedRootProduct?.id
        ? selectedRootProduct.id
        : getDefaultRootProduct(rootProducts).id;

    return (
        <>
            <div className={`${styles.root} configurator__option-container`}>
                <fieldset>
                    <legend>
                        <h6>{props.optionSet.root_product_selector.label}</h6>
                    </legend>
                    {showCompareLink(
                        props.isPlpPanelStyle,
                        props.optionSet,
                    ) && (
                        <>
                            <button
                                ref={compareTriggerRef}
                                className={styles.openBtn}
                                onClick={onOpen}
                            >
                                {t`Compare`}
                            </button>
                        </>
                    )}
                    {!props.isPlpPanelStyle && (
                        <RootProductCompareModal optionSet={props.optionSet} />
                    )}
                    {offeredProducts.map((rootProduct) => (
                        <label
                            className={
                                rootProduct.id === selectedProductID
                                    ? styles.selected
                                    : undefined
                            }
                            key={`${rootProduct.id}`}
                        >
                            <input
                                type="radio"
                                name="root-product"
                                value={isoProductID.unwrap(rootProduct.id)}
                                className={`ada-screenreader-only`}
                                checked={rootProduct.id === selectedProductID}
                                onChange={(e) => {
                                    if (!e.currentTarget.checked) {
                                        return;
                                    }
                                    onSelectRootProduct(rootProduct);
                                }}
                                aria-label={interpolate(t`Select %s`, [
                                    getRootProductTitle(
                                        rootProduct,
                                        props.isPlpPanelStyle,
                                    ),
                                ])}
                            />
                            {rootProduct.attributes
                                .configurator_root_product_selector_icon
                                ?.value && (
                                <img
                                    src={`${rootProduct.attributes.configurator_root_product_selector_icon.value}`}
                                    alt={interpolate(t`%s icon`, [
                                        getRootProductTitle(
                                            rootProduct,
                                            props.isPlpPanelStyle,
                                        ),
                                    ])}
                                />
                            )}
                            <span>
                                {getRootProductTitle(
                                    rootProduct,
                                    props.isPlpPanelStyle,
                                )}
                                {rootProduct.attributes.model_subtitle && (
                                    <span className={styles.subTitle}>
                                        {rootProduct.attributes.model_subtitle.value?.toString()}
                                    </span>
                                )}
                            </span>
                        </label>
                    ))}
                </fieldset>
            </div>
            {isPanelOpen && (
                <RootProductCompareDrawer
                    prefixNameCapped={
                        props.optionSet.root_product_selector.label
                    }
                    panelClass={"model"}
                    optionSet={props.optionSet}
                    isPanelOpen={isPanelOpen}
                    triggerRef={compareTriggerRef}
                    onCloseButtonClick={onCloseButtonClick}
                    useCompareDrawerCenter={props.useCompareDrawerCenter}
                />
            )}
        </>
    );
};
